import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Api, { getApiUrl } from '../../services/api';
import { useAtozasTranslate } from '../../context/AtozasLanguageContext';
import LanguageSelector from './LanguageSelector';
import ConfirmationModal from './ConfirmationModal';

const UDsidebar = ({ isOpen, onClose, user, driver, isDriver = false, onHome, onProfile, onRides, onLogout, onDriverTabChange, activeDriverTab, onBecomeDriver, onDriverDashboard, onUserDashboard }) => {
  const [driverProfile, setDriverProfile] = useState(driver);
  const [userProfile, setUserProfile] = useState(null); // Start with null to force fetch if needed
  const [rating, setRating] = useState({ average: 0, total: 0 });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const tLanguage = useAtozasTranslate('Language');
  const tEditProfile = useAtozasTranslate('Edit Profile');
  const tDashboard = useAtozasTranslate('Dashboard');
  const tMyRides = useAtozasTranslate('My Rides');
  const tEarnings = useAtozasTranslate('Earnings');
  const tAccount = useAtozasTranslate('Account');
  const tHome = useAtozasTranslate('Home');
  const tProfile = useAtozasTranslate('Profile');
  const tUserDashboard = useAtozasTranslate('User Dashboard');
  const tDriverDashboard = useAtozasTranslate('Driver Dashboard');
  const tBecomeDriver = useAtozasTranslate('Become Driver');
  const tLogout = useAtozasTranslate('Logout');

  // Fetch driver profile data if driver prop is missing profile picture
  useEffect(() => {
    const fetchDriverProfile = async () => {
      if (!isDriver || !user?.driverId || driverProfile?.profilePicture) return;
      
      try {
        const token = localStorage.getItem('uttaapp_token');
        const response = await axios.get(Api.Driver.profile(user.driverId), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success && response.data.driver) {
          setDriverProfile(response.data.driver);
        }
      } catch (error) {
        console.error('Error fetching driver profile for sidebar:', error);
      }
    };

    if (isOpen && isDriver) {
      fetchDriverProfile();
    }
  }, [isOpen, isDriver, user?.driverId, driverProfile?.profilePicture]);

  // Fetch user profile data if user prop is missing profile picture
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (isDriver || !isOpen) return;
      
      // Check if profile picture already exists in user prop
      if (user?.profilePicture) {
        console.log('User profile picture exists in prop:', user.profilePicture);
        // Update userProfile with user prop if it has profile picture
        if (!userProfile || userProfile.profilePicture !== user.profilePicture) {
          setUserProfile(user);
        }
        return;
      }
      
      // If userProfile already has profile picture, don't fetch again
      if (userProfile?.profilePicture) {
        console.log('User profile picture already fetched:', userProfile.profilePicture);
        return;
      }
      
      try {
        const token = localStorage.getItem('uttaapp_token');
        if (!token) {
          console.log('No token found for user profile fetch');
          return;
        }
        
        console.log('Fetching user profile for sidebar...');
        const response = await axios.get(Api.User.profile, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        console.log('User profile API response:', response.data);
        if (response.data.success && response.data.user) {
          console.log('User profile fetched:', response.data.user);
          console.log('User profile picture:', response.data.user.profilePicture);
          setUserProfile(response.data.user);
        }
      } catch (error) {
        console.error('Error fetching user profile for sidebar:', error);
      }
    };

    fetchUserProfile();
  }, [isOpen, isDriver, user, userProfile]);

  // Fetch rating (driver or user)
  useEffect(() => {
    const fetchRating = async () => {
      if (!isOpen) return;
      
      try {
        const token = localStorage.getItem('uttaapp_token');
        if (!token) {
          setRating({ average: 0, total: 0 });
          return;
        }

        if (isDriver && user?.driverId) {
          // Fetch driver rating
          const response = await axios.get(Api.Driver.rating, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.data.success) {
            setRating({
              average: response.data.averageRating || 0,
              total: response.data.totalRatings || 0
            });
          } else {
            setRating({ average: 0, total: 0 });
          }
        } else if (!isDriver && (user?.id || user?._id)) {
          // Fetch user rating
          const userId = user?.id || user?._id;
          const response = await axios.get(Api.Rating.getUserRatings(userId), {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.data.success) {
            setRating({
              average: parseFloat(response.data.averageRating) || 0,
              total: response.data.totalRatings || 0
            });
          } else {
            setRating({ average: 0, total: 0 });
          }
        } else {
          setRating({ average: 0, total: 0 });
        }
      } catch (error) {
        console.error('Error fetching rating:', error);
        setRating({ average: 0, total: 0 });
      }
    };

    fetchRating();
  }, [isOpen, isDriver, user?.driverId, user?.id, user?._id]);

  // Render stars component
  // CRITICAL: Use notranslate to prevent Google Translate from affecting star display
  const renderStars = (ratingValue) => {
    const stars = [];
    const fullStars = Math.floor(ratingValue);
    const hasHalfStar = ratingValue % 1 >= 0.5;
    
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <span key={i} className="text-yellow-400 text-lg notranslate" translate="no">★</span>
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <span key={i} className="text-yellow-400 text-lg notranslate" translate="no">☆</span>
        );
      } else {
        stars.push(
          <span key={i} className="text-gray-300 text-lg notranslate" translate="no">☆</span>
        );
      }
    }
    return stars;
  };

  if (!isOpen) return null;

  // Use fetched profiles if available, otherwise use props
  const currentDriver = driverProfile || driver;
  const currentUser = userProfile || user; // Use fetched profile or fallback to prop

  // Get user name
  const userName = currentUser?.username || currentUser?.name || 'User';

  // Get driver name
  const driverName = currentDriver?.name || currentUser?.username || currentUser?.name || 'Driver';

  // Helper function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Construct backend URL using centralized API helper
    const apiUrl = getApiUrl();
    
    // Remove leading slash if present to avoid double slashes
    const cleanPath = imagePath.replace(/^\/+/, '').replace(/\\/g, '/');
    
    return `${apiUrl}/${cleanPath}`;
  };

  // Get profile picture URL - check multiple possible field names
  const profilePictureUrl = isDriver 
    ? getImageUrl(currentDriver?.profilePicture || currentDriver?.profile_picture || currentDriver?.photo)
    : getImageUrl(currentUser?.profilePicture || currentUser?.profile_picture || currentUser?.photo);

  // Debug logging
  if (!isDriver) {
    console.log('Sidebar User Profile Debug:', {
      user: user,
      userProfile: userProfile,
      currentUser: currentUser,
      profilePicture: currentUser?.profilePicture,
      profile_picture: currentUser?.profile_picture,
      photo: currentUser?.photo,
      profilePictureUrl: profilePictureUrl
    });
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998] md:hidden"
        onClick={onClose}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      ></div>

      {/* Sidebar */}
      <div 
        className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl z-[9999] md:hidden transform transition-transform duration-300 ease-in-out overflow-y-auto rounded-r-3xl" 
        style={{ 
          maxHeight: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <div className="bg-primary-600 text-white p-6 pb-8 rounded-tr-3xl">
          {/* Back Button */}
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 transition-colors p-2 rounded-full mb-4"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* User/Driver Profile Section */}
          <div className="flex items-start space-x-4">
            {/* Profile Picture */}
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-2xl font-semibold overflow-hidden relative flex-shrink-0">
              {profilePictureUrl ? (
                <img 
                  src={profilePictureUrl} 
                  alt={isDriver ? driverName : userName}
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => {
                    // Hide image on error and show fallback
                    e.target.style.display = 'none';
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div 
                className="profile-fallback w-full h-full bg-white/20 rounded-full flex items-center justify-center"
                style={{ display: profilePictureUrl ? 'none' : 'flex' }}
              >
                {isDriver 
                  ? driverName.charAt(0).toUpperCase()
                  : userName.charAt(0).toUpperCase()
                }
              </div>
            </div>
            
            {/* Name, Rating Stars, and Edit Profile */}
            <div className="flex-1 min-w-0 pt-1">
              <h3 className="text-lg font-bold text-white mb-2 truncate">
                {isDriver ? driverName : userName}
              </h3>
              {/* Rating Stars - CRITICAL: Use notranslate to prevent Google Translate from breaking layout */}
              {rating.average > 0 && (
                <div className="flex items-center gap-1 mb-2 notranslate" translate="no" style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                  {renderStars(rating.average)}
                  {rating.total > 0 && (
                    <span className="text-xs text-white/70 ml-1 notranslate" translate="no" style={{ whiteSpace: 'nowrap' }}>
                      ({rating.total})
                    </span>
                  )}
                </div>
              )}
              <button
                onClick={() => {
                  if (isDriver && onDriverTabChange) {
                    onDriverTabChange('account');
                  } else if (onProfile) {
                    onProfile();
                  }
                  onClose();
                }}
                className="text-sm text-white/80 hover:text-white transition-colors"
              >
                {tEditProfile}
              </button>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="py-4">
          {/* Language Selector */}
          <div className="px-6 pb-4">
            <label className="block text-xs font-semibold text-gray-500 mb-2">
              {tLanguage}
            </label>
            <LanguageSelector
              aria-label={tLanguage}
              title={tLanguage}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="border-t border-gray-200 my-2"></div>

          {isDriver ? (
            <>
              {/* Driver Menu Items */}
              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('dashboard');
                  onClose();
                }}
                className={`w-full flex items-center px-6 py-4 text-left transition-colors ${
                  activeDriverTab === 'dashboard'
                    ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="font-medium text-base">{tDashboard}</span>
              </button>

              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('rides');
                  onClose();
                }}
                className={`w-full flex items-center px-6 py-4 text-left transition-colors ${
                  activeDriverTab === 'rides'
                    ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-medium text-base">{tMyRides}</span>
              </button>

              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('earnings');
                  onClose();
                }}
                className={`w-full flex items-center px-6 py-4 text-left transition-colors ${
                  activeDriverTab === 'earnings'
                    ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-base">{tEarnings}</span>
              </button>

              <button
                onClick={() => {
                  if (onDriverTabChange) onDriverTabChange('account');
                  onClose();
                }}
                className={`w-full flex items-center px-6 py-4 text-left transition-colors ${
                  activeDriverTab === 'account'
                    ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-medium text-base">{tAccount}</span>
              </button>
            </>
          ) : (
            <>
              {/* User Menu Items */}
              <button
                onClick={() => {
                  if (onHome) onHome();
                  onClose();
                }}
                className="w-full flex items-center px-6 py-4 text-left text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="font-medium text-base">{tHome}</span>
              </button>

              <button
                onClick={() => {
                  if (onRides) onRides();
                  onClose();
                }}
                className="w-full flex items-center px-6 py-4 text-left text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-medium text-base">{tMyRides}</span>
              </button>

              <button
                onClick={() => {
                  if (onProfile) onProfile();
                  onClose();
                }}
                className="w-full flex items-center px-6 py-4 text-left text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-medium text-base">{tProfile}</span>
              </button>
            </>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 my-2"></div>

          {/* Driver/User Dashboard Switch Options */}
          {isDriver ? (
            // Driver Dashboard - Show User Dashboard option
            onUserDashboard && (
              <button
                onClick={() => {
                  if (onUserDashboard) onUserDashboard();
                  onClose();
                }}
                className="w-full flex items-center px-6 py-4 text-left text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="font-medium text-base">{tUserDashboard}</span>
              </button>
            )
          ) : (
            // User Dashboard - Show Become Driver or Driver Dashboard option
            <>
              {user?.driverId ? (
                // Already registered as driver - Show Driver Dashboard
                onDriverDashboard && (
                  <button
                    onClick={() => {
                      if (onDriverDashboard) onDriverDashboard();
                      onClose();
                    }}
                    className="w-full flex items-center px-6 py-4 text-left text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="font-medium text-base">{tDriverDashboard}</span>
                  </button>
                )
              ) : (
                // Not registered - Show Become Driver
                onBecomeDriver && (
                  <button
                    onClick={() => {
                      if (onBecomeDriver) onBecomeDriver();
                      onClose();
                    }}
                    className="w-full flex items-center px-6 py-4 text-left text-primary-600 hover:bg-primary-50 transition-colors"
                  >
                    <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span className="font-medium text-base">{tBecomeDriver}</span>
                  </button>
                )
              )}
            </>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 my-2"></div>

          {/* Divider */}
          <div className="border-t border-gray-200 my-2"></div>

          {/* Logout Button */}
          <button
            onClick={() => {
              setShowLogoutConfirm(true);
            }}
            className="w-full flex items-center px-6 py-4 text-left text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <svg className="w-6 h-6 mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="font-medium text-base">{tLogout}</span>
          </button>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        show={showLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          if (onLogout) onLogout();
          onClose();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
};

export default UDsidebar;

